import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Container, Row, Col, Form, Alert } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import {
    FaUserMd,
    FaUserNurse,
    FaParking,
    FaClock,
    FaClinicMedical,
    FaStethoscope,
    FaNotesMedical,
    FaPhone,
    FaUser,
    FaPaperPlane,
    FaClipboardList,
    FaSmile
} from 'react-icons/fa';

const Evaluation = () => {
    const { t, i18n } = useTranslation();
    const [doctors, setDoctors] = useState([]);
    const [nurses, setNurses] = useState([]);
    const [formData, setFormData] = useState({
        doctorName: '',
        nurseName: '',
        parking: '',
        waitingTime: '',
        generalAppearance: '',
        doctorTime: '',
        treatmentPlan: '',
        nurseRating: '',
        additionalNotes: '',
        phone: '',
        patientName: ''
    });
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        const fetchTeam = async () => {
            try {
                const res = await axios.get('https://hasanhaji.com/api/team');
                const allTeam = res.data.data.teamMembers || [];

                const docs = allTeam.filter(m =>
                    (m.jobTitle && (m.jobTitle.toLowerCase().includes('doctor') || m.jobTitle.includes('طبيب'))) ||
                    (m.nameEn && m.nameEn.toLowerCase().includes('dr')) ||
                    (m.nameAr && m.nameAr.includes('د.'))
                );

                const nurs = allTeam.filter(m =>
                    (m.jobTitle && (m.jobTitle.toLowerCase().includes('nurse') || m.jobTitle.includes('ممرض'))) ||
                    !docs.includes(m)
                );

                setDoctors(docs.length > 0 ? docs : allTeam);
                setNurses(nurs.length > 0 ? nurs : allTeam);
            } catch (err) {
                console.error('Error fetching team', err);
            } finally {
                setLoading(false);
            }
        };
        fetchTeam();
    }, []);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setError('');
        try {
            await axios.post('https://hasanhaji.com/api/evaluations', formData);
            setSubmitted(true);
            window.scrollTo(0, 0);
        } catch (err) {
            console.error(err);
            setError(t('evaluation.errorMessage'));
        } finally {
            setSubmitting(false);
        }
    };

    const isRtl = i18n.language === 'ar';


    const RadioOption = ({ name, value, label, currentVal }) => (
        <div className="option-card">
            <input
                type="radio"
                name={name}
                value={value}
                checked={currentVal === value}
                onChange={handleChange}
                id={`${name}-${value}`}
            />
            <label htmlFor={`${name}-${value}`} className="option-label">
                {label}
            </label>
        </div>
    );

    if (submitted) {
        return (
            <div className="evaluation-section" dir={isRtl ? 'rtl' : 'ltr'}>
                <Container>
                    <Row className="justify-content-center">
                        <Col md={8} lg={6}>
                            <div className="evaluation-card success-card">
                                <div className="success-icon">
                                    <i className="fas fa-check"></i>
                                </div>
                                <h2 className="mb-3 fw-bold text-dark">{t('evaluation.successTitle')}</h2>
                                <p className="text-muted mb-5">{t('evaluation.successMessage')}</p>
                                <div className="d-grid gap-3">
                                    <Link to="/" className="btn btn-primary btn-lg rounded-pill">
                                        {t('evaluation.backToHome')}
                                    </Link>
                                    <Link to="/services" className="btn btn-outline-secondary btn-lg rounded-pill">
                                        {t('evaluation.browseServices')}
                                    </Link>
                                </div>
                            </div>
                        </Col>
                    </Row>
                </Container>
            </div>
        );
    }

    return (
        <div className="evaluation-section" dir={isRtl ? 'rtl' : 'ltr'}>
            <Container>
                <Row className="justify-content-center">
                    <Col md={10} lg={8}>
                        <div className="evaluation-card">
                            <div className="evaluation-header">
                                <h2 className="section-title">{t('evaluation.title')}</h2>
                                <p>{t('evaluation.subtitle')}</p>
                            </div>

                            <div className="evaluation-body">
                                {error && (
                                    <Alert variant="danger" className="mb-4 rounded-3 border-0 bg-danger bg-opacity-10 text-danger">
                                        {error}
                                    </Alert>
                                )}

                                <Form onSubmit={handleSubmit}>
                                    { }
                                    <div className="evaluation-group">
                                        <h5 className="mb-4 text-primary fw-bold">
                                            <FaUserMd className="me-2 ms-2" />
                                            {t('evaluation.medicalStaff')}
                                        </h5>
                                        <Row className="g-4">
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="evaluation-label">
                                                        <FaUserMd className="me-2 ms-2 text-muted" size={14} />
                                                        {t('evaluation.doctorLabel')} <span className="text-danger">{t('evaluation.required')}</span>
                                                    </Form.Label>
                                                    <Form.Select
                                                        name="doctorName"
                                                        value={formData.doctorName}
                                                        onChange={handleChange}
                                                        required
                                                        className="form-select-modern"
                                                    >
                                                        <option value="">{t('evaluation.selectPlaceholder')}</option>
                                                        {doctors.map(doc => (
                                                            <option key={doc.id} value={isRtl ? doc.nameAr : doc.nameEn}>
                                                                {isRtl ? doc.nameAr : doc.nameEn}
                                                            </option>
                                                        ))}
                                                    </Form.Select>
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="evaluation-label">
                                                        <FaUserNurse className="me-2 ms-2 text-muted" size={14} />
                                                        {t('evaluation.nurseLabel')} <span className="text-danger">{t('evaluation.required')}</span>
                                                    </Form.Label>
                                                    <Form.Select
                                                        name="nurseName"
                                                        value={formData.nurseName}
                                                        onChange={handleChange}
                                                        required
                                                        className="form-select-modern"
                                                    >
                                                        <option value="">{t('evaluation.selectPlaceholder')}</option>
                                                        {nurses.map(nurse => (
                                                            <option key={nurse.id} value={isRtl ? nurse.nameAr : nurse.nameEn}>
                                                                {isRtl ? nurse.nameAr : nurse.nameEn}
                                                            </option>
                                                        ))}
                                                    </Form.Select>
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                    </div>

                                    { }
                                    <div className="evaluation-group">
                                        <h5 className="mb-4 text-primary fw-bold">
                                            <FaClipboardList className="me-2 ms-2" />
                                            {t('evaluation.yourExperience')}
                                        </h5>

                                        <div className="mb-4">
                                            <label className="evaluation-label">
                                                <FaParking className="me-2 ms-2 text-muted" />
                                                {t('evaluation.parkingQuestion')}
                                            </label>
                                            <div className="options-grid">
                                                <RadioOption name="parking" value="Yes" label={t('evaluation.options.yes')} currentVal={formData.parking} />
                                                <RadioOption name="parking" value="No" label={t('evaluation.options.no')} currentVal={formData.parking} />
                                                <RadioOption name="parking" value="Sometimes" label={t('evaluation.options.sometimes')} currentVal={formData.parking} />
                                            </div>
                                        </div>

                                        <div className="mb-4">
                                            <label className="evaluation-label">
                                                <FaClock className="me-2 ms-2 text-muted" />
                                                {t('evaluation.waitingTimeQuestion')}
                                            </label>
                                            <div className="options-grid">
                                                <RadioOption name="waitingTime" value="Fast" label={t('evaluation.options.fast')} currentVal={formData.waitingTime} />
                                                <RadioOption name="waitingTime" value="Acceptable" label={t('evaluation.options.acceptable')} currentVal={formData.waitingTime} />
                                                <RadioOption name="waitingTime" value="Long" label={t('evaluation.options.long')} currentVal={formData.waitingTime} />
                                            </div>
                                        </div>

                                        <div className="mb-4">
                                            <label className="evaluation-label">
                                                <FaClinicMedical className="me-2 ms-2 text-muted" />
                                                {t('evaluation.generalAppearanceQuestion')}
                                            </label>
                                            <div className="options-grid">
                                                <RadioOption name="generalAppearance" value="Excellent" label={t('evaluation.options.excellent')} currentVal={formData.generalAppearance} />
                                                <RadioOption name="generalAppearance" value="Acceptable" label={t('evaluation.options.acceptable')} currentVal={formData.generalAppearance} />
                                                <RadioOption name="generalAppearance" value="Bad" label={t('evaluation.options.bad')} currentVal={formData.generalAppearance} />
                                            </div>
                                        </div>

                                        <div className="mb-4">
                                            <label className="evaluation-label">
                                                <FaStethoscope className="me-2 ms-2 text-muted" />
                                                {t('evaluation.doctorTimeQuestion')}
                                            </label>
                                            <div className="options-grid">
                                                <RadioOption name="doctorTime" value="Yes" label={t('evaluation.options.yes')} currentVal={formData.doctorTime} />
                                                <RadioOption name="doctorTime" value="Sometimes" label={t('evaluation.options.sometimes')} currentVal={formData.doctorTime} />
                                                <RadioOption name="doctorTime" value="No" label={t('evaluation.options.no')} currentVal={formData.doctorTime} />
                                            </div>
                                        </div>

                                        <div className="mb-4">
                                            <label className="evaluation-label">
                                                <FaClipboardList className="me-2 ms-2 text-muted" />
                                                {t('evaluation.treatmentPlanQuestion')}
                                            </label>
                                            <div className="options-grid">
                                                <RadioOption name="treatmentPlan" value="Yes" label={t('evaluation.options.yes')} currentVal={formData.treatmentPlan} />
                                                <RadioOption name="treatmentPlan" value="A little" label={t('evaluation.options.aLittle')} currentVal={formData.treatmentPlan} />
                                                <RadioOption name="treatmentPlan" value="No" label={t('evaluation.options.no')} currentVal={formData.treatmentPlan} />
                                            </div>
                                        </div>

                                        <div className="mb-4">
                                            <label className="evaluation-label">
                                                <FaSmile className="me-2 ms-2 text-muted" />
                                                {t('evaluation.nurseRatingQuestion')}
                                            </label>
                                            <div className="options-grid">
                                                <RadioOption name="nurseRating" value="Excellent" label={t('evaluation.options.excellent')} currentVal={formData.nurseRating} />
                                                <RadioOption name="nurseRating" value="Acceptable" label={t('evaluation.options.acceptable')} currentVal={formData.nurseRating} />
                                                <RadioOption name="nurseRating" value="Poor" label={t('evaluation.options.poor')} currentVal={formData.nurseRating} />
                                            </div>
                                        </div>
                                    </div>

                                    { }
                                    <div className="evaluation-group border-0">
                                        <h5 className="mb-4 text-primary fw-bold">
                                            <FaNotesMedical className="me-2 ms-2" />
                                            {t('evaluation.additionalInfo')}
                                        </h5>

                                        <Form.Group className="mb-4">
                                            <Form.Label className="evaluation-label">
                                                <FaNotesMedical className="me-2 ms-2 text-muted" />
                                                {t('evaluation.additionalNotesLabel')}
                                            </Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                rows={4}
                                                name="additionalNotes"
                                                value={formData.additionalNotes}
                                                onChange={handleChange}
                                                placeholder={t('evaluation.additionalNotesPlaceholder')}
                                                className="form-control-modern"
                                            />
                                        </Form.Group>

                                        <Row className="g-4">
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="evaluation-label">
                                                        <FaPhone className="me-2 ms-2 text-muted" />
                                                        {t('evaluation.phoneLabel')}
                                                    </Form.Label>
                                                    <Form.Control
                                                        type="tel"
                                                        name="phone"
                                                        value={formData.phone}
                                                        onChange={handleChange}
                                                        placeholder={t('evaluation.phonePlaceholder')}
                                                        className="form-control-modern"
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="evaluation-label">
                                                        <FaUser className="me-2 ms-2 text-muted" />
                                                        {t('evaluation.nameLabel')}
                                                    </Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        name="patientName"
                                                        value={formData.patientName}
                                                        onChange={handleChange}
                                                        placeholder={t('evaluation.namePlaceholder')}
                                                        className="form-control-modern"
                                                    />
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                    </div>

                                    <div className="mt-5">
                                        <button type="submit" disabled={submitting} className="btn-submit-modern">
                                            {submitting ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                    {t('evaluation.submitting')}
                                                </>
                                            ) : (
                                                <>
                                                    <FaPaperPlane className="me-2" />
                                                    {t('evaluation.submitButton')}
                                                </>
                                            )}
                                        </button>
                                    </div>

                                </Form>
                            </div>
                        </div>
                    </Col>
                </Row>
            </Container>
        </div>
    );
};

export default Evaluation;
